import { useMutation, UseMutationResult, useQuery, useQueryClient, useSuspenseQuery, UseSuspenseQueryResult } from '@tanstack/react-query'
import { GetOverseasPriorityRes, GetSchedulesGridReq, GetSchedulesGridRes, SetOverseasPriorityReq } from './types'
import {
  createCutOffSchedule,
  deleteCutOffSchedule,
  getOutboundSchedules,
  getOverseasPriority,
  setPriorityType,
  setOverseasPriority,
  updateScheduleStatus,
  updateSlotWaitTime,
  upsertGlobalCuttime,
} from './apis'
import { ScheduleMenuCode } from '@/utils/menuCodeHeaders'

export const outboundSchedulesQueryKeys = {
  all: ['OUTBOUND_SCHEDULES_V2'] as const,
  schedules: () => [...outboundSchedulesQueryKeys.all, 'SCHEDULES'] as const,
  schedule: (params: GetSchedulesGridReq) => [...outboundSchedulesQueryKeys.schedules(), params] as const,
  overseasPriority: () => [...outboundSchedulesQueryKeys.all, 'OVERSEAS_PRIORITY'] as const,
}

/** 일자별 출고 일정 조회 */
export const useGetOutboundSchedules = (params: GetSchedulesGridReq) => {
  const { data, ...rest } = useQuery({
    queryKey: outboundSchedulesQueryKeys.schedule(params),
    queryFn: () => getOutboundSchedules(params),
    select: (res) => res.data,
  })

  return { data, ...rest }
}

/** 컷오프 일정 생성 (현재 사용 X) */
export const useCreateCutOffSchedule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createCutOffSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedules() })
    },
  })
}

/** 컷오프 일정 삭제 (현재 사용 X) */
export const useDeleteCutOffSchedule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteCutOffSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedules() })
    },
  })
}

/** 출고지시 on/off 토글 (v2) — optimistic update + 실패 롤백 */
export const useUpdateScheduleStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateScheduleStatus,
    onMutate: async (req) => {
      // applyDate(=조회 날짜)로 해당 grid 캐시를 낙관적 갱신
      if (!req.applyDate) return
      const key = outboundSchedulesQueryKeys.schedule({ date: req.applyDate, shipperCode: req.shipperCode, cutType: req.cutType })
      await queryClient.cancelQueries({ queryKey: key })
      const prev = queryClient.getQueryData<ApiResponseV2<GetSchedulesGridRes>>(key)
      if (prev) {
        queryClient.setQueryData<ApiResponseV2<GetSchedulesGridRes>>(key, {
          ...prev,
          data: {
            ...prev.data,
            slots: prev.data.slots.map((slot) =>
              slot.startTime === req.startTime
                ? {
                    ...slot,
                    warehouses: slot.warehouses.map((w) => (w.warehouseCode === req.warehouseCode ? { ...w, isEnabled: req.isEnabled } : w)),
                  }
                : slot,
            ),
          },
        })
      }
      return { prev, key }
    },
    onError: (_error, _req, context) => {
      if (context?.prev && context?.key) {
        queryClient.setQueryData(context.key, context.prev)
      }
    },
    onSettled: (_data, _error, req) => {
      if (!req.applyDate) return
      queryClient.invalidateQueries({
        queryKey: outboundSchedulesQueryKeys.schedule({ date: req.applyDate, shipperCode: req.shipperCode, cutType: req.cutType }),
      })
    },
  })
}

/** 할당 우선순위 설정 (v2, D-12) — priority_type + warehouse priority 통합 */
export const useSetPriorityType = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: setPriorityType,
    onSuccess: (_, req) => {
      // applyDate=null(평시/base)은 모든 일자 그리드에 영향 → schedule 전체 무효화
      if (!req.applyDate) {
        queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedules() })
        return
      }
      queryClient.invalidateQueries({
        queryKey: outboundSchedulesQueryKeys.schedule({ date: req.applyDate, shipperCode: req.shipperCode, cutType: 'B2C' }),
      })
    },
  })
}

/** 해외 출고 우선순위 조회 (Suspense) */
export const useSuspenseGetOverseasPriority = (menuCode: ScheduleMenuCode): UseSuspenseQueryResult<GetOverseasPriorityRes, Error> => {
  const { data, ...rest } = useSuspenseQuery({
    queryKey: outboundSchedulesQueryKeys.overseasPriority(),
    queryFn: () => getOverseasPriority(menuCode),
    select: (res) => res.data,
  })

  return { data, ...rest }
}

/** 해외 출고 우선순위 설정 */
export const useSetOverseasPriority = (menuCode: ScheduleMenuCode): UseMutationResult<unknown, Error, SetOverseasPriorityReq> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: SetOverseasPriorityReq) => setOverseasPriority(params, menuCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.overseasPriority() })
    },
  })
}

/** 시간대별 Wait Time 설정 (v2) */
export const useUpdateSlotWaitTime = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateSlotWaitTime,
    onSuccess: (_, req) => {
      if (!req.applyDate) return
      queryClient.invalidateQueries({
        queryKey: outboundSchedulesQueryKeys.schedule({ date: req.applyDate, shipperCode: req.shipperCode, cutType: req.cutType }),
      })
    },
  })
}

/** 컷 마감시간 설정 (v2) */
export const useUpsertGlobalCuttime = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: upsertGlobalCuttime,
    // applyDate=null(상시)도 현재 조회 그리드에 반영되어야 하므로 schedule 전체 무효화
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedules() })
    },
  })
}
